import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { CountdownTimer } from './components/CountdownTimer';
import { ExpandableCard } from './components/ExpandableCard';
import { GoogleMap } from './components/GoogleMap';
import { HorizontalTimeline } from './components/HorizontalTimeline';
import { ImageSlider } from './components/ImageSlider';
import { weddingData, mockSubmitForm } from './mock';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Separator } from './components/ui/separator';
import { Heart, MapPin, Phone, Mail, Users, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import heroBackground from './hero-background.jpg';
import heroBackgroundMobile from './hero-background-mobile.jpg';

function App() {
  const [showAttendingForm, setShowAttendingForm] = useState(false);
  const [showAbsentForm, setShowAbsentForm] = useState(false);
  const [stickyButtonOpacity, setStickyButtonOpacity] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const rsvpButtonRef = useRef(null);
  const [attendingForm, setAttendingForm] = useState({
    lastName: '',
    firstName: '',
    phone: '',
    accompanied: '',
    plusOneLastName: '',
    plusOneFirstName: '',
    dietaryRestriction: '',
    allergyDetails: '',
    accommodationSaturday: '',
    sundayLunch: '',
    sundayEvening: '',
    accommodationSunday: '',
    message: ''
  });
  const [absentForm, setAbsentForm] = useState({
    lastName: '',
    firstName: '',
    phone: '',
    message: ''
  });

  const weddingDate = new Date('2026-07-11T15:00:00').getTime();

  // Effect to detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to handle sticky button opacity based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (rsvpButtonRef.current) {
        const rsvpButton = rsvpButtonRef.current;
        const rsvpButtonRect = rsvpButton.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Position du bouton permanent RSVP par rapport au viewport
        const rsvpButtonTop = rsvpButtonRect.top;
        const rsvpButtonBottom = rsvpButtonRect.bottom;
        
        // Zone de proximité : 200px avant et après le bouton
        const proximityZone = 300;
        
        // Position du bouton sticky (fixe en bas)
        const stickyButtonPosition = windowHeight - 40; // 40px from bottom
        
        // Calculer la distance entre le sticky et le bouton RSVP
        let distance;
        if (rsvpButtonTop > stickyButtonPosition) {
          // Le bouton RSVP est en dessous du sticky
          distance = rsvpButtonTop - stickyButtonPosition;
        } else if (rsvpButtonBottom < stickyButtonPosition) {
          // Le bouton RSVP est au-dessus du sticky
          distance = stickyButtonPosition - rsvpButtonBottom;
        } else {
          // Le sticky est au niveau du bouton RSVP
          distance = 0;
        }
        
        // Calculer l'opacité (0 quand distance = 0, 1 quand distance >= proximityZone)
        let opacity = Math.min(distance / proximityZone, 1);
        
        setStickyButtonOpacity(opacity);
      }
    };
    
    // Écouter le scroll
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAttendingSubmit = async (e) => {
    e.preventDefault();
    try {
      // URL du webhook Google Apps Script
      const scriptURL = 'https://script.google.com/macros/s/AKfycbyt70c8sbEiEqJro1AH9VCREPDxpyqqCPMKc1pfRGFt7ncGiNIPuLqiLCH1kjWC-ZF1/exec';
      
      // Créer un FormData avec les données du formulaire
      const formData = new FormData();
      formData.append('lastName', attendingForm.lastName);
      formData.append('firstName', attendingForm.firstName);
      formData.append('phone', attendingForm.phone);
      formData.append('accompanied', attendingForm.accompanied);
      formData.append('plusOneLastName', attendingForm.plusOneLastName);
      formData.append('plusOneFirstName', attendingForm.plusOneFirstName);
      formData.append('dietaryRestriction', attendingForm.dietaryRestriction);
      formData.append('allergyDetails', attendingForm.allergyDetails);
      formData.append('accommodationSaturday', attendingForm.accommodationSaturday);
      formData.append('sundayLunch', attendingForm.sundayLunch);
      formData.append('sundayEvening', attendingForm.sundayEvening);
      formData.append('accommodationSunday', attendingForm.accommodationSunday);
      formData.append('message', attendingForm.message);
      
      const response = await fetch(scriptURL, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
      
      toast.success("Tes infos ont bien été enregistrées", {
        description: "Merci pour ta confirmation ! 💖",
        duration: 5000,
      });
      setAttendingForm({
        lastName: '',
        firstName: '',
        phone: '',
        accompanied: '',
        plusOneLastName: '',
        plusOneFirstName: '',
        dietaryRestriction: '',
        allergyDetails: '',
        accommodationSaturday: '',
        sundayLunch: '',
        sundayEvening: '',
        accommodationSunday: '',
        message: ''
      });
      setShowAttendingForm(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi", {
        description: error.message || "Veuillez réessayer plus tard.",
        duration: 5000,
      });
    }
  };

  const handleAbsentSubmit = async (e) => {
    e.preventDefault();
    try {
      // URL du webhook Google Apps Script
      const scriptURL = 'https://script.google.com/macros/s/AKfycbyt70c8sbEiEqJro1AH9VCREPDxpyqqCPMKc1pfRGFt7ncGiNIPuLqiLCH1kjWC-ZF1/exec';
      
      // Créer un FormData avec les données du formulaire
      const formData = new FormData();
      formData.append('lastName', absentForm.lastName);
      formData.append('firstName', absentForm.firstName);
      formData.append('phone', absentForm.phone);
      formData.append('accompanied', 'ABSENT');
      formData.append('message', absentForm.message);
      
      const response = await fetch(scriptURL, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
      
      toast.success("Ton message a bien été enregistré", {
        description: "On te remercie de nous avoir prévenus 💙",
        duration: 5000,
      });
      setAbsentForm({
        lastName: '',
        firstName: '',
        phone: '',
        message: ''
      });
      setShowAbsentForm(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi", {
        description: error.message || "Veuillez réessayer plus tard.",
        duration: 5000,
      });
    }
  };

  return (
    <div className="App">
      {/* Section 1: Hero */}
      <section 
        className="hero-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${isMobile ? heroBackgroundMobile : heroBackground})`
        }}
      >
        <div className="hero-overlay">
          <div className="hero-card">
            <h1 className="hero-title">Claire & Thomas</h1>
            <p className="hero-date">{weddingData.couple.weddingDate}</p>
            <div className="hero-divider"></div>
            <p className="hero-venue">{weddingData.couple.venue}</p>
          </div>
        </div>
      </section>

      {/* Section 2: Countdown */}
      <section className="countdown-section">
        <div className="container">
          <CountdownTimer targetDate={weddingDate} />
        </div>
      </section>

      {/* Section 3: When & Where */}
      <section className="when-where-section">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ExpandableCard
              title="QUAND ?"
              summary={weddingData.couple.weddingDetails}
              details={[
                weddingData.schedule.ceremony,
                weddingData.schedule.cocktail,
                weddingData.schedule.dinner,
                weddingData.schedule.party
              ]}
              type="when"
            />
            
            <ExpandableCard
              title="OÙ ?"
              summary={weddingData.couple.venueDetails}
              details={[weddingData.location.address]}
              type="where"
            >
              <GoogleMap 
                lat={weddingData.location.coordinates.lat} 
                lng={weddingData.location.coordinates.lng} 
              />
            </ExpandableCard>
          </div>
        </div>
      </section>

      {/* Section 4: RSVP */}
      <section className="rsvp-section">
        <div className="container">
          <div className="rsvp-buttons-container">
            {/* Attending CTA */}
            <button
              ref={rsvpButtonRef}
              onClick={() => {
                setShowAttendingForm(!showAttendingForm);
                if (!showAttendingForm) setShowAbsentForm(false);
              }}
              className="rsvp-cta-button rsvp-cta-primary"
            >
              Je confirme ma présence
            </button>
            
            {showAttendingForm && (
              <form onSubmit={handleAttendingSubmit} className="rsvp-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="lastName" className="rsvp-label">Nom *</Label>
                    <Input
                      id="lastName"
                      className="rsvp-input"
                      value={attendingForm.lastName}
                      onChange={(e) => setAttendingForm({...attendingForm, lastName: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="firstName" className="rsvp-label">Prénom *</Label>
                    <Input
                      id="firstName"
                      className="rsvp-input"
                      value={attendingForm.firstName}
                      onChange={(e) => setAttendingForm({...attendingForm, firstName: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="rsvp-field-spacing-phone">
                  <Label htmlFor="phone" className="rsvp-label">Numéro de téléphone *</Label>
                  <div className="rsvp-form-field-phone">
                    <Input
                      id="phone"
                      type="tel"
                      className="rsvp-input"
                      value={attendingForm.phone}
                      onChange={(e) => setAttendingForm({...attendingForm, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="rsvp-field-spacing">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.8rem' }}>
                    <Label className="rsvp-label" style={{ marginBottom: 0 }}>Je serai accompagné.e *</Label>
                    <div className="info-badge-container">
                      <span className="info-badge">
                        Mariage sans enfant :-)
                        <div className="info-tooltip">
                          <div className="info-tooltip-content">
                            ✨ Nous adorons vos petits bouts, mais afin de profiter tous ensemble d'une soirée sereine et festive, nous avons choisi de célébrer notre mariage sans enfants. Merci de votre compréhension 💛
                          </div>
                        </div>
                      </span>
                    </div>
                  </div>
                  <RadioGroup
                    value={attendingForm.accompanied}
                    onValueChange={(value) => setAttendingForm({...attendingForm, accompanied: value})}
                    className="rsvp-radio-group"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="oui" id="accompanied-oui" />
                      <Label htmlFor="accompanied-oui">Oui</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="non" id="accompanied-non" />
                      <Label htmlFor="accompanied-non">Non</Label>
                    </div>
                  </RadioGroup>
                </div>

                {attendingForm.accompanied === 'oui' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 rsvp-field-spacing">
                    <div>
                      <Label htmlFor="plusOneLastName" className="rsvp-label">Nom de votre +1</Label>
                      <Input
                        id="plusOneLastName"
                        className="rsvp-input"
                        value={attendingForm.plusOneLastName}
                        onChange={(e) => setAttendingForm({...attendingForm, plusOneLastName: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="plusOneFirstName" className="rsvp-label">Prénom de votre +1</Label>
                      <Input
                        id="plusOneFirstName"
                        className="rsvp-input"
                        value={attendingForm.plusOneFirstName}
                        onChange={(e) => setAttendingForm({...attendingForm, plusOneFirstName: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                <div className="rsvp-field-spacing">
                  <Label className="rsvp-label">L'un de vous a-t-il un régime alimentaire particulier ou des allergies ?</Label>
                  <RadioGroup
                    value={attendingForm.dietaryRestriction}
                    onValueChange={(value) => setAttendingForm({...attendingForm, dietaryRestriction: value, allergyDetails: value !== 'allergies' ? '' : attendingForm.allergyDetails})}
                    className="rsvp-radio-group"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="aucun" id="diet-aucun" />
                      <Label htmlFor="diet-aucun">Aucun</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="vegetarien" id="diet-vegetarien" />
                      <Label htmlFor="diet-vegetarien">Végétarien</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="vegetalien" id="diet-vegetalien" />
                      <Label htmlFor="diet-vegetalien">Végétalien</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="allergies" id="diet-allergies" />
                      <Label htmlFor="diet-allergies">Allergies</Label>
                    </div>
                  </RadioGroup>
                </div>

                {attendingForm.dietaryRestriction === 'allergies' && (
                  <div className="rsvp-field-spacing">
                    <Label htmlFor="allergyDetails" className="rsvp-label">Précisez vos allergies</Label>
                    <Textarea
                      id="allergyDetails"
                      className="rsvp-input"
                      value={attendingForm.allergyDetails}
                      onChange={(e) => setAttendingForm({...attendingForm, allergyDetails: e.target.value})}
                      placeholder="Décrivez vos allergies..."
                      rows={3}
                    />
                  </div>
                )}

                <div className="rsvp-field-spacing">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.8rem' }}>
                    <Label className="rsvp-label" style={{ marginBottom: 0 }}>Besoin de se loger le samedi soir ?*</Label>
                    <div className="info-badge-container">
                      <span className="info-badge info-badge-secondary">
                        Infos sur les couchages
                        <div className="info-tooltip">
                          <div className="info-tooltip-content info-tooltip-housing">
                            <h4 style={{ margin: '0 0 0.8rem 0', fontSize: '0.85rem', fontWeight: '400', fontStyle: 'italic' }}>2 options pour les logements</h4>
                            <p style={{ margin: 0, fontStyle: 'italic' }}>
                              Les gites sur place ne nous permettent pas d'accueillir la totalité des invités. C'est pourquoi nous recensons les besoins de chacun afin d'organiser au mieux les logements.
                              <br/><br/>
                              <strong style={{ fontStyle: 'normal' }}>• Pour ceux qui dorment sur place :</strong> une participation à hauteur de 45€/pers./nuit vous sera demandée
                              <br/><br/>
                              <strong style={{ fontStyle: 'normal' }}>• Pour ceux qui seront logés à l'extérieur :</strong> nous pouvons vous recommander des gîtes/hôtels situés à quelques minutes du domaine, vous réserverez en direct.
                            </p>
                          </div>
                        </div>
                      </span>
                    </div>
                  </div>
                  <RadioGroup
                    value={attendingForm.accommodationSaturday}
                    onValueChange={(value) => setAttendingForm({...attendingForm, accommodationSaturday: value})}
                    className="rsvp-radio-group"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="oui" id="accom-sat-oui" />
                      <Label htmlFor="accom-sat-oui">Oui</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="non" id="accom-sat-non" />
                      <Label htmlFor="accom-sat-non">Non</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="rsvp-field-spacing">
                  <Label className="rsvp-label">Présent.e.s pour le retour, le dimanche midi *</Label>
                  <RadioGroup
                    value={attendingForm.sundayLunch}
                    onValueChange={(value) => setAttendingForm({...attendingForm, sundayLunch: value})}
                    className="rsvp-radio-group"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="oui" id="sunday-lunch-oui" />
                      <Label htmlFor="sunday-lunch-oui">Oui</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="non" id="sunday-lunch-non" />
                      <Label htmlFor="sunday-lunch-non">Non</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="rsvp-field-spacing">
                  <Label className="rsvp-label">Présent.e.s pour le retour du retour, le dimanche soir *</Label>
                  <RadioGroup
                    value={attendingForm.sundayEvening}
                    onValueChange={(value) => setAttendingForm({...attendingForm, sundayEvening: value})}
                    className="rsvp-radio-group"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="oui" id="sunday-evening-oui" />
                      <Label htmlFor="sunday-evening-oui">Oui</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="non" id="sunday-evening-non" />
                      <Label htmlFor="sunday-evening-non">Non</Label>
                    </div>
                  </RadioGroup>
                </div>

                {attendingForm.sundayEvening === 'oui' && (
                  <div className="rsvp-field-spacing">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.8rem' }}>
                      <Label className="rsvp-label" style={{ marginBottom: 0 }}>Besoin de se loger le dimanche soir ?*</Label>
                      <div className="info-badge-container">
                        <span className="info-badge info-badge-secondary">
                          Infos sur les couchages
                          <div className="info-tooltip">
                            <div className="info-tooltip-content info-tooltip-housing">
                              <h4 style={{ margin: '0 0 0.8rem 0', fontSize: '0.85rem', fontWeight: '400', fontStyle: 'italic' }}>2 options pour les logements</h4>
                              <p style={{ margin: 0, fontStyle: 'italic' }}>
                                Les gites sur place ne nous permettent pas d'accueillir la totalité des invités. C'est pourquoi nous recensons les besoins de chacun afin d'organiser au mieux les logements.
                                <br/><br/>
                                <strong style={{ fontStyle: 'normal' }}>• Pour ceux qui dorment sur place :</strong> une participation à hauteur de 45€/pers./nuit vous sera demandée
                                <br/><br/>
                                <strong style={{ fontStyle: 'normal' }}>• Pour ceux qui seront logés à l'extérieur :</strong> nous pouvons vous recommander des gîtes/hôtels situés à quelques minutes du domaine, vous réserverez en direct.
                              </p>
                            </div>
                          </div>
                        </span>
                      </div>
                    </div>
                    <RadioGroup
                      value={attendingForm.accommodationSunday}
                      onValueChange={(value) => setAttendingForm({...attendingForm, accommodationSunday: value})}
                      className="rsvp-radio-group"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="oui" id="accom-sun-oui" />
                        <Label htmlFor="accom-sun-oui">Oui</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="non" id="accom-sun-non" />
                        <Label htmlFor="accom-sun-non">Non</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                <div className="rsvp-form-textarea-spacing">
                  <Label htmlFor="message" className="rsvp-label">Un mot à ajouter ?</Label>
                  <Textarea
                    id="message"
                    className="rsvp-textarea"
                    value={attendingForm.message}
                    onChange={(e) => setAttendingForm({...attendingForm, message: e.target.value})}
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  className="rsvp-form-button bg-gradient-to-r from-fuchsia-500 to-orange-500 hover:from-fuchsia-600 hover:to-orange-600 text-white font-bold"
                >
                  Je confirme, hâte d'y être ! 😊
                </Button>
              </form>
            )}

            {/* Absent CTA */}
            <button
              onClick={() => {
                setShowAbsentForm(!showAbsentForm);
                if (!showAbsentForm) setShowAttendingForm(false);
              }}
              className="rsvp-cta-button rsvp-cta-secondary"
            >
              Je suis malheureusement absent.e 😔
            </button>
            
            {showAbsentForm && (
              <form onSubmit={handleAbsentSubmit} className="rsvp-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="absentLastName" className="rsvp-label">Nom</Label>
                    <Input
                      id="absentLastName"
                      className="rsvp-input"
                      value={absentForm.lastName}
                      onChange={(e) => setAbsentForm({...absentForm, lastName: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="absentFirstName" className="rsvp-label">Prénom</Label>
                    <Input
                      id="absentFirstName"
                      className="rsvp-input"
                      value={absentForm.firstName}
                      onChange={(e) => setAbsentForm({...absentForm, firstName: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="rsvp-field-spacing-phone">
                  <Label htmlFor="absentPhone" className="rsvp-label">Numéro de téléphone</Label>
                  <div className="rsvp-form-field-phone">
                    <Input
                      id="absentPhone"
                      type="tel"
                      className="rsvp-input"
                      value={absentForm.phone}
                      onChange={(e) => setAbsentForm({...absentForm, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="rsvp-form-textarea-spacing">
                  <Label htmlFor="absentMessage" className="rsvp-label">Un mot à ajouter ?</Label>
                  <Textarea
                    id="absentMessage"
                    className="rsvp-textarea"
                    value={absentForm.message}
                    onChange={(e) => setAbsentForm({...absentForm, message: e.target.value})}
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  className="rsvp-form-button rsvp-form-button-secondary border-gray-400 text-gray-600 hover:bg-gray-100"
                >
                  <span className="hidden md:inline">J'ai le cœur lourd mais je valide 😔</span>
                  <span className="md:hidden">Je valide (le cœur lourd 😔)</span>
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Section 5: Our Story */}
      <section className="story-section">
        <div className="container">
          <h2 className="section-title">Notre histoire</h2>
          <HorizontalTimeline />
          <div className="flex justify-center mt-8">
            <ImageSlider />
          </div>
        </div>
      </section>

      {/* Section 6: Contacts */}
      <section className="contacts-section">
        <div className="container">
          <h2 className="section-title">CONTACTS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="contact-card">
              <CardHeader>
                <div className="contact-icon-large">
                  <Heart size={48} />
                </div>
                <CardTitle className="contact-card-title">
                  Les (futurs) mariés
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="contact-names">
                  <div className="contact-person">
                    <Phone size={18} className="text-black" />
                    <div>
                      <div className="contact-person-name">{weddingData.contacts.couple.claire.name}</div>
                      <div className="contact-person-phone">{weddingData.contacts.couple.claire.phone}</div>
                    </div>
                  </div>
                  <div className="contact-person">
                    <Phone size={18} className="text-black" />
                    <div>
                      <div className="contact-person-name">{weddingData.contacts.couple.thomas.name}</div>
                      <div className="contact-person-phone">{weddingData.contacts.couple.thomas.phone}</div>
                    </div>
                  </div>
                </div>
                <div className="contact-email-container">
                  <div className="contact-email-icon">
                    <Mail size={18} />
                  </div>
                  <a href={`mailto:${weddingData.contacts.couple.email}`} className="contact-email">
                    {weddingData.contacts.couple.email}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="contact-card">
              <CardHeader>
                <div className="contact-icon-large">
                  <Users size={48} />
                </div>
                <CardTitle className="contact-card-title">
                  Les (supers) témoins
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="contact-witnesses-grid">
                  <div className="contact-witness-name">Justine</div>
                  <div className="contact-witness-name">Audrey</div>
                  <div className="contact-witness-name">Florian</div>
                  <div className="contact-witness-name">Julien</div>
                </div>
                <div className="contact-email-container">
                  <div className="contact-email-icon">
                    <Mail size={18} />
                  </div>
                  <a href={`mailto:${weddingData.contacts.witnesses.email}`} className="contact-email">
                    {weddingData.contacts.witnesses.email}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 7: Game */}
      <section className="game-section">
        <div className="container">
          <h2 className="section-title">Joue avec nous pour patienter !</h2>
          <div className="game-content">
            <h3 className="game-subtitle">La récolte des Mariés</h3>
            <p className="game-description">
              Tu disposes de 30 secondes pour récolter un maximum de bonus tout en évitant les malus !<br />
              Attrape les supers bonus pour gagner un peu de temps !
            </p>
            <a 
              href="https://www.claire-et-thomas.fr/jeu" 
              target="_blank" 
              rel="noopener noreferrer"
              className="game-cta-button"
            >
              Go ! 🎮
            </a>
          </div>
        </div>
      </section>

      {/* Section 8: FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Des questions ?</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {weddingData.faq.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                <AccordionTrigger className="text-left font-semibold text-gray-800 hover:text-fuchsia-600">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 whitespace-pre-line">
                  {item.answer}
                  {item.question === 'Dress code, thème à respecter ?' && (
                    <div style={{ marginTop: '1rem' }}>
                      <img 
                        src="/color-hearts.png" 
                        alt="Palette de couleurs" 
                        style={{ 
                          height: '40px',
                          width: 'auto',
                          display: 'block'
                        }} 
                      />
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Toaster />
      
      {/* Sticky RSVP Button */}
      <button
        onClick={() => {
          setShowAttendingForm(!showAttendingForm);
          if (!showAttendingForm) setShowAbsentForm(false);
          // Scroll to RSVP section
          const rsvpSection = document.querySelector('.rsvp-section');
          if (rsvpSection) {
            rsvpSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        className="sticky-rsvp-button"
        style={{ 
          opacity: (showAttendingForm || showAbsentForm) ? 0 : stickyButtonOpacity,
          pointerEvents: (showAttendingForm || showAbsentForm || stickyButtonOpacity < 0.1) ? 'none' : 'auto'
        }}
      >
        Je confirme ma présence
      </button>

      {/* Game Widget */}
      <a 
        href="https://www.claire-et-thomas.fr/jeu" 
        target="_blank" 
        rel="noopener noreferrer"
        className="game-widget"
        title="Jouer à La récolte des Mariés"
      >
        🎮
      </a>
    </div>
  );
}

export default App;